import {Injectable} from '@angular/core';
import Bugsnag from "@bugsnag/js";
import {HttpClient} from "@angular/common/http";
import {environment} from "@environments/environment";
import {BehaviorSubject} from 'rxjs';

export interface IValidationResponse {
  isValid: boolean;
  explication?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ValidateCommentsAIService {

  BASE_URL = environment.apiUrl
  
  // Track l'état de validation IA pour chaque question
  questionAIValidationStatus$ = new BehaviorSubject<Record<string, boolean>>({});

  constructor(
    private http: HttpClient,
  ) {
  }

  validateAnswerWithOpenAI(question: string, answer: string, lang: string, selectedStatement?: string): Promise<IValidationResponse> {
    return new Promise((resolve, reject) => {
      if (!question || !answer) {
        return resolve({isValid: true});
      }
      this.http.post(`${this.BASE_URL}/appCommunity/publicActions`,
        {action: 'validateWithAI', params: {question, answer, lang, selectedStatement}})
        .subscribe({
          next: (response: any) => {
            try {
              // Vérifier la structure de la réponse
              if (!response || !response.data || !response.data.datas) {
                console.error('[Validation] Structure de réponse invalide', { response });
                return resolve({isValid: true, explication: 'Erreur: structure de réponse invalide'});
              }

              const datas = response.data.datas;

              // Si la réponse contient déjà isValid (cas d'erreur backend ou réponse directe)
              if (datas.isValid !== undefined) {
                return resolve({
                  isValid: datas.isValid,
                  explication: datas.explanation || datas.explication || (datas.error ? 'Erreur de validation' : '')
                });
              }

              // Vérifier la structure OpenAI standard
              if (!datas.choices || !Array.isArray(datas.choices) || datas.choices.length === 0) {
                console.error('[Validation] Pas de choices dans la réponse', { datas });
                return resolve({isValid: true, explication: 'Erreur: réponse OpenAI invalide'});
              }

              const firstChoice = datas.choices[0];
              if (!firstChoice || !firstChoice.message || !firstChoice.message.content) {
                console.error('[Validation] Structure choice invalide', { firstChoice });
                return resolve({isValid: true, explication: 'Erreur: structure de choix invalide'});
              }

              // Parser le contenu JSON
              const content = firstChoice.message.content;
              try {
                const parsed = JSON.parse(content);
                resolve({
                  isValid: parsed.isValid !== false, // Par défaut true si non spécifié
                  explication: parsed.explication || parsed.explanation || ''
                });
              } catch (parseError) {
                console.error('[Validation] Erreur parsing JSON', { content, error: parseError });
                // Si le parsing échoue, on accepte la réponse par défaut
                resolve({isValid: true, explication: 'Erreur de parsing de la réponse'});
              }
            } catch (error) {
              console.error('[Validation] Erreur traitement réponse', { error, response });
              // En cas d'erreur, on accepte la réponse par défaut pour ne pas bloquer l'utilisateur
              resolve({isValid: true, explication: 'Erreur lors de la validation'});
            }
          },
          error: err => {
            console.error('[Validation] AIValidation Error', err);
            Bugsnag.notify(err, (event) => {
              event.addMetadata('ValidateCommentsAIService', {
                method: 'validateAnswerWithOpenAI',
                body: {action: 'validateWithAI', params: {question, answer, lang, selectedStatement}},
                endpoint: `${this.BASE_URL}/appCommunity/publicActions`
              })
            });
            // En cas d'erreur HTTP, on accepte la réponse par défaut
            resolve({isValid: true, explication: 'Erreur de connexion lors de la validation'});
          },
          complete: () => {
          }
        })
    })
  }

  updateQuestionAIValidationStatus(questionKey: string, isValid: boolean) {
    const currentStatus = this.questionAIValidationStatus$.value;
    this.questionAIValidationStatus$.next({
      ...currentStatus,
      [questionKey]: isValid
    });
  }

  getQuestionAIValidationStatus(questionKey: string): boolean {
    return this.questionAIValidationStatus$.value[questionKey] !== false;
  }


}
